import { DefaultScoreValueMapping, DefaultSurveyQuestionOptions, QuestionSummary, SelectionReport, SelectionSummaryWrapper, Survey, SurveyQuestion, SurveyReport, SurveySegment, SurveyTheme } from "../../DTOs";
import { segmentListIsInLists } from "./segmentationHelpers";
import { ISelectionStats, ScoredSelectionStats } from "./SelectionStats";
import { surveyQuestionHelper } from "../../helpers/surveyQuestionHelper";
import { assertNever } from "../../util";

export type QuestionReportDataSet = {
    question:SurveyQuestion,
    themeIndex:number,
    themeQuestionIndex:number,
    absoluteQuestionIndex:number,
    stats:ISelectionStats,
    commentCount:number
}

export type ThemeReportDataSet = {
    theme:SurveyTheme,
    themeIndex:number,
    stats:ScoredSelectionStats
    questions:QuestionReportDataSet[]
}

export type IndicatorReportDataSet = {
    indicator:string,
    questions:QuestionReportDataSet[],
    stats:ScoredSelectionStats
}

export type SurveyReportDataSet = {
    themeData:ThemeReportDataSet[],
    indicatorData:IndicatorReportDataSet[]
    stats:ScoredSelectionStats
}

export class SurveyReportBuilder
{
    constructor(
        public survey:Survey, 
        public surveyReport:SurveyReport)
    {
        if(!this.survey)
            throw new Error("SurveyReportBuilder: survey cannot be null or undefined");

        if(!this.surveyReport)
            throw new Error("SurveyReportBuilder: surveyReport cannot be null or undefined");
    }

    static fromSelectionReports(survey:Survey, reports:SelectionReport[]):SurveyReportBuilder
    {
        const surveyReport:SurveyReport = {
            id: 'temp',
            surveyId: survey.id,
            selectionReports: reports
        };
        return new SurveyReportBuilder(survey, surveyReport);
    }

    static getDataFromSingleQuestionSummaryList(survey:Survey, questionSummaries:QuestionSummary[]):SurveyReportDataSet
    {
        return new SingleSegmentSurveyReportBuilder(survey, questionSummaries).getReport();
    }

    getData(filter?:SurveySegment[][]):SurveyReportDataSet
    {
        const aggregatedData = this.buildSingleQuestionSummaryList(filter);

        const data = new SingleSegmentSurveyReportBuilder(this.survey, aggregatedData).getReport();
        return data;
    }

    buildSingleQuestionSummaryList(filter?:SurveySegment[][]):QuestionSummary[]
    {
        const allQuestions = this.survey.themes.flatMap(t => t.questions);

        const selectedSegmentReports = this.getSelectedSegmentReports(filter);

        // Create a list of QuestionSummary objects, one for each question in the survey, with no data.
        if(selectedSegmentReports.length === 0)
        {
            return allQuestions.map(q => ({
                selectionSummary: SelectionSummaryWrapper.createFromQuestion(q).data,
                commentCount: 0
            }));
        }

        if(selectedSegmentReports.some(r => r.questionIndexedSummaries.length !== allQuestions.length))
            throw new Error("Mismatched number of questions and question reports. Expected=" + allQuestions.length);

        const questionSummaries:QuestionSummary[] = [];
        for(let questionIndex=0; questionIndex<allQuestions.length; questionIndex++)
        {
            // Will throw if option values in the selection summaries do not match
            const combinedQuestionSummary = SelectionSummaryWrapper.aggregate(
                selectedSegmentReports.map(r => r.questionIndexedSummaries[questionIndex].selectionSummary)
            ).data;
            const combinedCommentCount = selectedSegmentReports.reduce((acc, r) => acc + r.questionIndexedSummaries[questionIndex].commentCount, 0);

            questionSummaries.push({
                selectionSummary: combinedQuestionSummary,
                commentCount: combinedCommentCount
            });
        }

        return questionSummaries;
    }

    private getSelectedSegmentReports(filter?:SurveySegment[][]):SelectionReport[]
    {
        const selectedSegmentReports = filter 
            ? this.surveyReport.selectionReports.filter(r => segmentListIsInLists(filter, r.segments)) 
            : this.surveyReport.selectionReports;

        this.assertQuestionSummaryLengths(selectedSegmentReports);
        return selectedSegmentReports;
    }

    private assertQuestionSummaryLengths(selectionReports:SelectionReport[]):void
    {
        const expectedQuestionCount = this.getSurveyQuestionCount();
        const mismatchedReport = selectionReports.find(r => r.questionIndexedSummaries.length !== expectedQuestionCount);
        if(!mismatchedReport)
            return;

        const segmentText = mismatchedReport.segments.length === 0
            ? "[]"
            : mismatchedReport.segments.map(s => `${s.name}:${s.value}`).join(", ");

        throw new Error(
            `Mismatched number of questions and question reports. Expected=${expectedQuestionCount}, Actual=${mismatchedReport.questionIndexedSummaries.length}, Segments=[${segmentText}]`
        );
    }

    private getSurveyQuestionCount():number
    {
        return this.survey.themes.reduce((acc, theme) => acc + theme.questions.length, 0);
    }
}

export class SingleSegmentSurveyReportBuilder
{
    constructor(
        public survey:Survey,
        public questionIndexedSummaries:QuestionSummary[]
    )
    {
        this.validateArguments();
    }

    private validateArguments(): void
    {
        if(!this.survey)
            throw new Error("Survey is required");

        if(!this.questionIndexedSummaries)
            throw new Error("questionIndexedSummaries is required");

        const allQuestions = this.survey.themes.flatMap(t => t.questions);
        if(this.questionIndexedSummaries.length !== allQuestions.length)
            throw new Error("questionIndexedSummaries length does not match the number of questions in the survey");

        for(let i=0; i<allQuestions.length; i++)
        {
            const question = allQuestions[i];
            const summary = this.questionIndexedSummaries[i];
            if(question.type === 'default')
            {
                for(const oc of summary.selectionSummary.optionValueCounts)
                {
                    if(!DefaultSurveyQuestionOptions.some(o => o.value === oc.value))
                        throw new Error(`Invalid option value for question at question index ${i}: ${JSON.stringify(oc)}`);
                }
            }
            else if(question.type === 'custom')
            {
                for(const oc of summary.selectionSummary.optionValueCounts)
                {
                    if(!question.options.some(o => o.value === oc.value))
                        throw new Error(`Invalid option value for question at question index ${i}: ${JSON.stringify(oc)}`);
                }
            }
            else if(question.type === 'multiple-choice')
            {
                for(const oc of summary.selectionSummary.optionValueCounts)
                {
                    if(!question.options.some(o => o.value === oc.value))
                        throw new Error(`Invalid option value for question at question index ${i}: ${JSON.stringify(oc)}`);
                }
            }
            else
            {
                assertNever(question);
                throw new Error(`Unknown question type at question index ${i}: ${JSON.stringify(question)}`);
            }
        }
    }

    getReport():SurveyReportDataSet
    {
        const themeData = this.getThemeReport();
        const indicatorData = this.getIndicatorReport();
        return {
            themeData,
            indicatorData,
            stats: ScoredSelectionStats.aggregate(
                themeData.length === 0
                    ? [SelectionSummaryWrapper.fromDefaultCounts(0,0,0,0,0).data]
                    : themeData.map(t => t.stats.data),
                DefaultScoreValueMapping
            )
        };
    }

    private getIndicatorReport():IndicatorReportDataSet[]
    {
        const indicatorDataSets:IndicatorReportDataSet[] = [];

        let absoluteIndex = 0;
        for(let i=0; i<this.survey.themes.length; i++)
        {
            for(let j=0; j<this.survey.themes[i].questions.length; j++)
            {
                const question = this.survey.themes[i].questions[j];

                for(let k=0; k<question.indicators.length; k++)
                {
                    const indicator = question.indicators[k];

                    let existingIndicatorData = indicatorDataSets.find(d => d.indicator === indicator);
                    if(!existingIndicatorData)
                    {
                        existingIndicatorData = {
                            indicator,
                            questions:[],
                            stats:ScoredSelectionStats.fromDefaultCounts(0,0,0,0,0)
                        };
                        indicatorDataSets.push(existingIndicatorData);
                    }

                    const summary = this.questionIndexedSummaries[absoluteIndex].selectionSummary;
                    const stats = surveyQuestionHelper.toSelectionStats(question, summary);
                
                    existingIndicatorData.questions.push({
                        question,
                        themeIndex:i,
                        themeQuestionIndex:j,
                        absoluteQuestionIndex:absoluteIndex,
                        stats,
                        commentCount:this.questionIndexedSummaries[absoluteIndex].commentCount
                    })

                    if(stats instanceof ScoredSelectionStats)
                    {
                        const asDefaultStats = ScoredSelectionStats.toDefaultQuestionValues(stats);
                        existingIndicatorData.stats = ScoredSelectionStats.aggregate([existingIndicatorData.stats.data, asDefaultStats.data], DefaultScoreValueMapping);
                    }
                }
                absoluteIndex++;
            }
        }

        return indicatorDataSets;
    }

    private getThemeReport():ThemeReportDataSet[]
    {
        const themeDataSets:ThemeReportDataSet[] = [];

        let absoluteIndex = 0;
        for(let i=0; i<this.survey.themes.length; i++)
        {
            const themeData:ThemeReportDataSet = {
                theme:this.survey.themes[i],
                themeIndex:i,
                stats: new ScoredSelectionStats(
                    SelectionSummaryWrapper.fromDefaultCounts(0,0,0,0,0).data,
                    DefaultScoreValueMapping
                ),
                questions:[]
            };
            
            for(let j=0; j<this.survey.themes[i].questions.length; j++)
            {
                const question = this.survey.themes[i].questions[j];

                const summary = this.questionIndexedSummaries[absoluteIndex].selectionSummary;

                const stats = surveyQuestionHelper.toSelectionStats(question, summary);

                const questionData:QuestionReportDataSet = {
                    question,
                    themeIndex:i,
                    themeQuestionIndex:j,
                    absoluteQuestionIndex:absoluteIndex,
                    stats,
                    commentCount:this.questionIndexedSummaries[absoluteIndex].commentCount
                };

                themeData.questions.push(questionData);

                if(stats instanceof ScoredSelectionStats)
                {
                    const asDefaultStats = ScoredSelectionStats.toDefaultQuestionValues(stats);
                    themeData.stats = ScoredSelectionStats.aggregate([themeData.stats.data, asDefaultStats.data], DefaultScoreValueMapping);
                }
                
                absoluteIndex++;
            }

            themeDataSets.push(themeData);
        }
        return themeDataSets;
    }
}